'use client'

import { useState, useEffect, useContext, useCallback, useMemo } from 'react'
import { Button } from '@/components/ui/shadcn/button'
import { Input } from '@/components/ui/shadcn/input'
import { Label } from '@/components/ui/shadcn/label'
import { Checkbox } from '@/components/ui/shadcn/checkbox'
import { Separator } from '@/components/ui/shadcn/separator'
import { Skeleton } from '@/components/ui/shadcn/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/shadcn/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/shadcn/select'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/shadcn/tooltip'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { Info, AlertCircle } from 'lucide-react'
import ErrorAlerts from '../feedback/ErrorAlerts'
import { AuthContext } from '@/contexts/AuthContext'
import {
  applyRoleTemplate,
  getTemplateOptions,
} from '@/lib/utils/roleTemplates'
import {
  fetchPermissionMetadata,
  transformPermissionMetadata,
} from '@/lib/api/permissions'

const TEMPLATE_NONE = 'none'

const prePopulate = (data, fields = []) => {
  for (const key of fields) {
    if (key === 'name') continue
    data[key] = false
  }
  return data
}

const toFormData = permissions => {
  const keys = Object.keys(permissions)
  const newData = {}
  prePopulate(newData)

  for (const key of keys) {
    if (typeof permissions[key] === 'object') {
      for (const subkey of Object.keys(permissions[key])) {
        newData[key + '[' + subkey + ']'] = permissions[key][subkey]
      }
    } else {
      newData[key] = permissions[key]
    }
  }

  return newData
}

const checkAll = (data, setData, fields = []) => {
  const newData = { ...data }
  for (const key of fields) {
    if (key === 'name') continue
    newData[key] = true
  }
  setData(newData)
}

const checkNone = (data, setData, fields = []) => {
  const newData = { ...data }
  for (const key of fields) {
    if (key === 'name') continue
    newData[key] = false
  }
  setData(newData)
}

const PermissionsSection = ({
  permissions,
  title,
  description,
  data,
  isEdit,
  handlePermissionChange,
}) => {
  const authContext = useContext(AuthContext)
  const canEdit = !isEdit || authContext.user.isAuthorized('editRoles')

  return (
    <div className="rounded-md border bg-card p-4">
      <div className="mb-3">
        <h5 className="text-sm font-semibold">{title}</h5>
        {description && (
          <p className="text-xs text-muted-foreground">{description}</p>
        )}
      </div>
      <div className="grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-2">
        {permissions.map(permission => (
          <div key={permission.name} className="flex items-center gap-2">
            <Checkbox
              id={`perm-${permission.name}`}
              name={permission.name}
              checked={!!data[permission.name]}
              disabled={!canEdit}
              onCheckedChange={checked =>
                handlePermissionChange(permission.name, !!checked)
              }
            />
            <Label
              htmlFor={`perm-${permission.name}`}
              className="cursor-pointer font-normal"
            >
              {permission.label}
            </Label>
            {permission.description && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="size-3.5 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent>{permission.description}</TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

export default function CreateRoleModal({
  open,
  onClose,
  loading,
  submit,
  error,
  errors,
  edit,
}) {
  const [isOpen, setIsOpen] = useState(false)
  const [data, setData] = useState({})
  const [selectedTemplate, setSelectedTemplate] = useState(TEMPLATE_NONE)
  const [permissionMetadata, setPermissionMetadata] = useState(null)
  const [loadingMetadata, setLoadingMetadata] = useState(false)
  const [metadataError, setMetadataError] = useState(null)
  const authContext = useContext(AuthContext)

  const canEdit = !edit || authContext.user.isAuthorized('editRoles')

  useEffect(() => {
    if (open && !permissionMetadata) {
      setLoadingMetadata(true)
      fetchPermissionMetadata(authContext)
        .then(metadata => {
          setPermissionMetadata(metadata)
          const { allPermissionNames } = transformPermissionMetadata(metadata)
          if (!edit) {
            setData(prePopulate({}, allPermissionNames))
          }
        })
        .catch(err => {
          console.error('Failed to load permission metadata:', err)
          setMetadataError('Failed to load permission definitions')
        })
        .finally(() => setLoadingMetadata(false))
    }
  }, [open, permissionMetadata, authContext, edit])

  useEffect(() => {
    if (!open && isOpen) {
      const { allPermissionNames } = transformPermissionMetadata(
        permissionMetadata || {},
      )
      setData(prePopulate({}, allPermissionNames))
      setSelectedTemplate(TEMPLATE_NONE)
    }
    setIsOpen(open)
  }, [open, isOpen, permissionMetadata])

  useEffect(() => {
    if (edit) {
      setData({ name: edit.name, id: edit.id, ...toFormData(edit.permissions) })
      setSelectedTemplate(TEMPLATE_NONE)
    } else if (permissionMetadata) {
      const { allPermissionNames } =
        transformPermissionMetadata(permissionMetadata)
      setData(prePopulate({}, allPermissionNames))
    }
  }, [edit, permissionMetadata])

  const handleTemplateChange = useCallback(
    templateKey => {
      setSelectedTemplate(templateKey)
      if (templateKey && templateKey !== TEMPLATE_NONE) {
        const templateData = applyRoleTemplate(templateKey)
        if (templateData) {
          const existingName = data.name || ''
          setData({ ...templateData, name: existingName })
        }
      }
    },
    [data.name],
  )

  const handlePermissionChange = useCallback((permName, checked) => {
    setData(prevData => ({
      ...prevData,
      [permName]: checked,
    }))
  }, [])

  const { sections: permissionSections, allPermissionNames } = useMemo(
    () => transformPermissionMetadata(permissionMetadata || {}),
    [permissionMetadata],
  )

  const title = edit ? (canEdit ? 'Edit Role' : 'View Role') : 'Create Role'

  return (
    <Dialog
      open={open}
      onOpenChange={v => {
        if (!v) onClose()
      }}
    >
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription className="sr-only">
            Configure role permissions
          </DialogDescription>
        </DialogHeader>

        <ErrorAlerts
          error={error}
          errors={errors}
          message="Something went wrong with your submission:"
        />

        <form onSubmit={e => submit(e, edit)} className="space-y-4">
          {!edit && (
            <>
              <div className="space-y-2">
                <Label>Start with Template (Optional)</Label>
                <Select
                  value={selectedTemplate}
                  onValueChange={handleTemplateChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="None - Start from scratch" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TEMPLATE_NONE}>
                      None - Start from scratch
                    </SelectItem>
                    {getTemplateOptions().map(option => (
                      <SelectItem key={option.value} value={option.value}>
                        <div>
                          <p className="text-sm">{option.label}</p>
                          <p className="text-xs text-muted-foreground">
                            {option.description}
                          </p>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {selectedTemplate && selectedTemplate !== TEMPLATE_NONE && (
                <Alert>
                  <Info className="size-4" />
                  <AlertDescription>
                    Template applied! You can now customize the permissions as
                    needed.
                  </AlertDescription>
                </Alert>
              )}
            </>
          )}

          <div className="space-y-2">
            <Label htmlFor="role-name">Role Name</Label>
            <Input
              id="role-name"
              name="name"
              value={data.name || ''}
              onChange={e => setData(d => ({ ...d, name: e.target.value }))}
              disabled={!canEdit}
              required
              placeholder={
                selectedTemplate && selectedTemplate !== TEMPLATE_NONE
                  ? 'Enter a custom name for this role'
                  : ''
              }
            />
          </div>

          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => checkAll(data, setData, allPermissionNames)}
              disabled={!canEdit || loadingMetadata}
            >
              Check All Boxes
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => checkNone(data, setData, allPermissionNames)}
              disabled={!canEdit || loadingMetadata}
            >
              Uncheck All Boxes
            </Button>
          </div>

          {loadingMetadata ? (
            <div className="space-y-3">
              <Skeleton className="h-48 w-full" />
              <Skeleton className="h-48 w-full" />
            </div>
          ) : metadataError ? (
            <Alert variant="destructive">
              <AlertCircle className="size-4" />
              <AlertDescription>{metadataError}</AlertDescription>
            </Alert>
          ) : (
            <div className="space-y-3">
              {permissionSections.map(section => (
                <PermissionsSection
                  key={section.name}
                  title={section.title}
                  description={section.description}
                  data={data}
                  isEdit={!!edit}
                  permissions={section.permissions}
                  handlePermissionChange={handlePermissionChange}
                />
              ))}
            </div>
          )}

          <Separator />

          <DialogFooter className="flex-col gap-2 sm:flex-col">
            <Button
              type="submit"
              className="w-full"
              disabled={loading || !canEdit}
            >
              {loading ? 'Saving...' : edit ? 'Update' : 'Create'}
            </Button>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={onClose}
            >
              {edit && !canEdit ? 'Close' : 'Cancel'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
